import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, EyeIcon, EditIcon, TrashIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { BankAccount, BankAccountFilters, BankAccountType, BankAccountStatus } from '../../types/bank-account.types';
import { bankAccountService } from '../../services/bank-account.service';
export function BankAccountList() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<BankAccountFilters>({
    page: 1,
    limit: 20
  });
  useEffect(() => {
    loadAccounts();
  }, [filters]);
  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await bankAccountService.getBankAccounts(filters);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to load bank accounts:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this bank account?')) {
      try {
        await bankAccountService.deleteBankAccount(id);
        loadAccounts();
      } catch (error: any) {
        alert(error.message);
      }
    }
  };
  const typeOptions = [{
    value: '',
    label: 'All Types'
  }, {
    value: BankAccountType.SAVINGS,
    label: 'Savings'
  }, {
    value: BankAccountType.CURRENT,
    label: 'Current'
  }, {
    value: BankAccountType.FIXED_DEPOSIT,
    label: 'Fixed Deposit'
  }, {
    value: BankAccountType.OTHER,
    label: 'Other'
  }];
  const statusOptions = [{
    value: '',
    label: 'All Status'
  }, {
    value: BankAccountStatus.ACTIVE,
    label: 'Active'
  }, {
    value: BankAccountStatus.INACTIVE,
    label: 'Inactive'
  }, {
    value: BankAccountStatus.CLOSED,
    label: 'Closed'
  }];
  const getStatusVariant = (status: string): 'success' | 'warning' | 'danger' => {
    const map: Record<string, 'success' | 'warning' | 'danger'> = {
      Active: 'success',
      Inactive: 'warning',
      Closed: 'danger'
    };
    return map[status] || 'warning';
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-345">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-346">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-347">
          <div data-id="element-348">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-349">
              Bank Account Management
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-350">
              Manage bank accounts for transactions
            </p>
          </div>
          <div className="flex gap-2" data-id="element-351">
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} data-id="element-352">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-353" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            <Button onClick={() => navigate('/bank-accounts/new')} data-id="element-354">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-355" />
              Add Bank Account
            </Button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && <div className="bg-white p-4 rounded-lg border space-y-4" data-id="element-356">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-357">
              Search Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-358">
              <Input label="Search" placeholder="Bank Name, Account No, Alias..." value={filters.search || ''} onChange={e => setFilters({
            ...filters,
            search: e.target.value
          })} data-id="element-359" />

              <Select label="Account Type" options={typeOptions} value={filters.type || ''} onChange={e => setFilters({
            ...filters,
            type: e.target.value as BankAccountType
          })} data-id="element-360" />

              <Select label="Status" options={statusOptions} value={filters.status || ''} onChange={e => setFilters({
            ...filters,
            status: e.target.value as BankAccountStatus
          })} data-id="element-361" />
            </div>
            <div className="flex justify-end" data-id="element-362">
              <Button variant="secondary" size="sm" onClick={() => setFilters({
            page: 1,
            limit: 20
          })} data-id="element-363">
                Clear Filters
              </Button>
            </div>
          </div>}

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden" data-id="element-364">
          {loading ? <div className="p-12 text-center text-gray-500" data-id="element-365">
              Loading bank accounts...
            </div> : <div className="overflow-x-auto" data-id="element-366">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-367">
                <thead className="bg-gray-50" data-id="element-368">
                  <tr data-id="element-369">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-370">
                      Bank Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-371">
                      Branch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-372">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-373">
                      Account No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-374">
                      Alias
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-375">
                      Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-376">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-377">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-378">
                  {accounts.length === 0 ? <tr data-id="element-379">
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500" data-id="element-380">
                        No bank accounts found. Click "Add Bank Account" to
                        create one.
                      </td>
                    </tr> : accounts.map(account => <tr key={account.id} className="hover:bg-gray-50" data-id="element-381">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-382">
                          {account.bankName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-383">
                          {account.branch}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-384">
                          {account.type}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 font-mono" data-id="element-385">
                          {account.accountNo}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-386">
                          {account.alias}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-387">
                          {account.address}
                        </td>
                        <td className="px-6 py-4" data-id="element-388">
                          <Badge variant={getStatusVariant(account.status)} data-id="element-389">
                            {account.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4" data-id="element-390">
                          <div className="flex items-center justify-center gap-2" data-id="element-391">
                            <button onClick={() => navigate(`/bank-accounts/${account.id}`)} className="text-gray-600 hover:text-gray-900" title="View" data-id="element-392">
                              <EyeIcon className="w-4 h-4" data-id="element-393" />
                            </button>
                            <button onClick={() => navigate(`/bank-accounts/${account.id}/edit`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-394">
                              <EditIcon className="w-4 h-4" data-id="element-395" />
                            </button>
                            <button onClick={() => handleDelete(account.id)} className="text-red-600 hover:text-red-800" title="Delete" data-id="element-396">
                              <TrashIcon className="w-4 h-4" data-id="element-397" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}